/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, useRef } from 'react';
import { Drawer } from 'vaul';
import { cn } from '@/lib/utils';
import { useKeyboardInset } from './use-keyboard-inset';

interface FlyoutShellProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Accessible label for the sheet (sr-only). */
    title: string;
    /**
     * Rendered between the drag handle and the scrollable body, *outside* the scroll area, so it
     * stays put when iOS shrinks the sheet to fit above the on-screen keyboard. Anything the user
     * must keep seeing while typing belongs here — see the search field in `search-flyout.tsx`.
     *
     * **Shrink contract:** this region is allowed to shrink (`min-h-0`), and flexbox gives it *all* of
     * the sheet's shortfall because the body below it has `flex-basis: 0`. So the region must itself
     * mark the element that must survive as `shrink-0` and leave something else shrinkable to absorb
     * the loss (search pins the form and lets its heading clip). Whatever stays `shrink-0` is the
     * incompressible floor: if handle + that floor exceeds the sheet height, it renders below the
     * sheet's bottom edge — behind the keyboard.
     */
    pinned?: ReactNode;
    /**
     * Classes for the body wrapper — in practice the sheet's minimum height. It goes on the body and
     * never on `Drawer.Content`; see the iOS keyboard note below.
     */
    bodyClassName?: string;
    children: ReactNode;
}

/**
 * Shared bottom-sheet chrome for the header flyouts (UCR-2762) — a vaul `Drawer` that flies up from
 * the bottom of the screen with a drag handle, over a 30% black overlay, on the Organic White surface.
 * vaul provides swipe-down-to-dismiss and tap-outside-to-close. Consumers fill the scrollable body,
 * and may pin a region above it via `pinned`.
 *
 * Three iOS constraints are load-bearing here (UCR-2984) — iOS shrinks only the *visual* viewport for
 * the keyboard, so nothing expressed in `vh`/`dvh` reacts to it:
 *
 * 1. **Never put a `min-height` on `Drawer.Content`.** When the keyboard opens vaul writes an inline
 *    `height` sized to the space above it plus `bottom: <keyboardHeight>` to lift the sheet clear. A
 *    `min-height` clamps the used height regardless of specificity, so the sheet would stay full-size
 *    *and* be lifted — pushing its top edge (and the search field) off-screen. Minimum heights go on
 *    the body instead, where they turn into a scroll rather than an overflow.
 * 2. **The body needs `touch-action: pan-y`.** vaul's injected CSS sets `touch-action: none` on the
 *    drawer, and the effective value is the intersection down the ancestor chain, so without this the
 *    body cannot scroll on iOS at all and a downward swipe becomes a dismiss drag.
 * 3. **`autoFocus` belongs on `Drawer.Root`, not on the field.** vaul's default suppresses Radix's
 *    focus-on-open entirely; passing it here focuses the first focusable node *after* the open
 *    animation, on a settled layout. It also routes that focus through Radix's `FocusScope`, which
 *    focuses with `preventScroll` — the reason *opening* the sheet never scrolls the page (see rule 4).
 * 4. **Both keyboard behaviours are ours, not vaul's** — {@link useKeyboardInset} owns the sheet's
 *    `bottom`/`height`/`max-height` (vaul's own handler leaks keyboard-up geometry once the field
 *    blurs, stranding the sheet mid-screen) *and* re-focuses a tapped field with
 *    `preventScroll: true` (without it WebKit scrolls the field to the centre of the visual viewport,
 *    sliding the whole page). Read that hook before touching the sheet's height or its focus handling.
 *
 * If the page shifts again, start from the hook — not from vaul's `disablePreventScroll`, which looks
 * like the lever and is not: the prop is inverted in vaul 1.1.2 so its mobile-Safari hijack is already
 * enabled by default, and that hijack prevented neither half of UCR-2984.
 */
export default function FlyoutShell({
    open,
    onOpenChange,
    title,
    pinned,
    bodyClassName = 'min-h-[50dvh]',
    children,
}: FlyoutShellProps): ReactElement {
    const contentRef = useRef<HTMLDivElement>(null);
    useKeyboardInset(contentRef, open);

    return (
        <Drawer.Root open={open} onOpenChange={onOpenChange} autoFocus>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 z-50 bg-black/30" />
                <Drawer.Content
                    ref={contentRef}
                    data-slot="header-flyout"
                    className="fixed inset-x-0 bottom-0 z-50 flex max-h-[67dvh] flex-col rounded-t-2xl bg-bm-organic-white text-bm-black outline-none">
                    <Drawer.Title className="sr-only">{title}</Drawer.Title>
                    {/* Drag handle (Figma: 93×3px pill) — the visual affordance for swipe-to-close. */}
                    <div className="flex shrink-0 justify-center py-3">
                        <div aria-hidden className="h-[3px] w-[93px] rounded-full bg-[#d1d1d1]" />
                    </div>
                    {/* `min-h-0` (rather than `shrink-0`) lets the pinned region give up space when the
                        sheet gets short — see the shrink contract in the JSDoc above. */}
                    {pinned ? <div className="flex min-h-0 flex-col px-6">{pinned}</div> : null}
                    {/* `min-h-0` lets this shrink below its content once vaul gives the sheet a fixed
                        height, so the body scrolls instead of spilling out under the keyboard. */}
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 [touch-action:pan-y]">
                        <div className={cn('flex flex-1 flex-col', bodyClassName)}>{children}</div>
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
}
